import { useEffect } from 'react';
import useForceUpdate from 'src/hooks/useForceUpdate';
import { shouldLog } from 'utils/LoggingUtil';

export enum LogLevel {
  Trace = 0,
  Debug,
  Info,
  Warn,
  Error,
}

export interface LogEntry {
  /** A unique ID for this log entry. */
  id: number;
  time: number;
  level: LogLevel;
  message: string;
}

/**
 * CircularLog stores a circular buffer of log entries.
 *
 * Note that this structure is *not* immutable.  An immutable structure here
 * could churn a lot of memory and trigger the garbage collector when we're writing
 * lots of logs.  In order to minimize GC overhead, we use a circular
 * buffer here - `entries` contains log entires.  `nextEntry` is the index the
 * next entry will be written to.  The index of the first entry in the log is
 * `nextEntry % entries.length`, and the most recent entry is
 * `(nextEntry - 1) % entries.length`.
 *
 * You can subscribe to a log to listen for changes if you want to render it in
 * React via `useRerenderOnLogUpdated()`.
 */
export class CircularLog {
  private _maxLogEntires: number;
  private _logKey: string | undefined;
  private nextEntry: number = 0;
  private nextId: number = 0;
  private entries: LogEntry[] = [];
  private subscribers: (() => void)[] = [];

  constructor(options: { consoleLogKey?: string; maxLogEntries: number }) {
    this._logKey = options.consoleLogKey;
    this._maxLogEntires = options.maxLogEntries;
  }

  /**
   * The maximum number of entries this log will keep.
   */
  public get maxLogEntries(): number {
    return this._maxLogEntires;
  }

  public set maxLogEntries(maxLogEntries: number) {
    this._maxLogEntires = maxLogEntries;
    this.entries = this.getOrderedLog().slice(-maxLogEntries);
    this.nextEntry = this.entries.length >= maxLogEntries ? 0 : this.entries.length;
    this._triggerSubscribers();
  }

  private _log(level: LogLevel, ...params: any[]): void {
    const message = params
      .map((param) => {
        if (typeof param === 'string') {
          return param;
        } else if (param === null) {
          return 'null';
        } else if (typeof param === 'object') {
          if (param instanceof Error) {
            const errString = param.toString();
            const stack = param.stack;
            if (stack?.startsWith(errString)) {
              // If the stack trace starts with the error message, don't include the stack trace.
              // This happens on Chrome.
              return stack;
            }
            return `${errString}\n${param.stack}`;
          } else if (param instanceof Date) {
            return param.toISOString();
          } else if (param?.type === 'video' && param.data) {
            return `video segment of length ${param.data.length}`;
          }
          return '[object]';
        }

        return `${param}`;
      })
      .join(' ');

    this.entries[this.nextEntry++] = {
      id: this.nextId++,
      time: Date.now(),
      level,
      message,
    };

    if (this.nextEntry >= this._maxLogEntires) {
      this.nextEntry = 0;
    }

    if (this.nextId === Number.MAX_SAFE_INTEGER) {
      this.nextId = 0;
    }

    this._triggerSubscribers();
  }

  private _triggerSubscribers() {
    for (const subscriber of this.subscribers) {
      subscriber();
    }
  }

  /** Returns the given log entries in time-series order. */
  getOrderedLog(): readonly LogEntry[] {
    const firstIndex = this.nextEntry % this.entries.length;
    if (firstIndex === 0) {
      return this.entries;
    }

    return this.entries.slice(firstIndex).concat(this.entries.slice(0, firstIndex));
  }

  /**
   * Calls `fn` on each log entry in time-series order.
   */
  map<K>(fn: (entry: LogEntry, index: number) => K): K[] {
    const firstIndex = this.nextEntry % this.entries.length;
    if (firstIndex === 0) {
      return this.entries.map(fn);
    }

    return this.entries.slice(firstIndex).map(fn).concat(this.entries.slice(0, firstIndex).map(fn));
  }

  trace(...params: any[]): void {
    if (shouldLog(this._logKey)) {
      console.trace(...params);
    }
    this._log(LogLevel.Trace, ...params);
  }

  debug(...params: any[]): void {
    if (shouldLog(this._logKey)) {
      console.debug(...params);
    }
    this._log(LogLevel.Debug, ...params);
  }

  info(...params: any[]): void {
    if (shouldLog(this._logKey)) {
      console.log(...params);
    }
    this._log(LogLevel.Info, ...params);
  }

  warn(...params: any[]): void {
    if (shouldLog(this._logKey)) {
      console.warn(...params);
    }
    this._log(LogLevel.Warn, ...params);
  }

  error(...params: any[]): void {
    if (shouldLog(this._logKey)) {
      console.error(...params);
    }
    this._log(LogLevel.Error, ...params);
  }

  log(...params: any[]): void {
    this.info(...params);
  }

  /**
   * Calls `fn` whenever this log changes.
   *
   * @param fn A function to call when the log changes.
   * @returns a function that unsubscribes from the log.
   */
  subscribe(fn: () => void): () => void {
    this.subscribers.push(fn);

    return () => {
      this.subscribers = this.subscribers.filter((subscriber) => subscriber !== fn);
    };
  }
}

/**
 * Forces a rerender whenever the given log changes.
 */
export function useRerenderOnLogUpdated(log: CircularLog): void {
  const rerender = useForceUpdate();

  useEffect(() => {
    return log.subscribe(rerender);
  }, [log, rerender]);
}
